import React from 'react';

interface HeadRawHtmlRendererProps {
  html: string;
}

interface ParsedElement {
  tagName: string;
  attributes: Record<string, string>;
  content?: string;
  key: string;
}

/**
 * Server-side safe HTML parser that uses regex instead of DOM APIs
 * Parses head elements for server-side rendering
 */
const parseHeadElementsSSR = (html: string): ParsedElement[] => {
  const elements: ParsedElement[] = [];

  // Regex patterns for different head elements
  const patterns = {
    link: /<link\s+([^>]*?)(?:\s*\/)?>(?:<\/link>)?/gi,
    meta: /<meta\s+([^>]*?)(?:\s*\/)?>(?:<\/meta>)?/gi,
    script: /<script\s+([^>]*?)>(.*?)<\/script>|<script\s+([^>]*?)(?:\s*\/)?>/gi,
    style: /<style\s+([^>]*?)>(.*?)<\/style>/gi,
    title: /<title\s*([^>]*?)>(.*?)<\/title>/gi,
  };

  // Helper function to parse attributes from attribute string
  const parseAttributes = (attrString: string): Record<string, string> => {
    const attributes: Record<string, string> = {};

    // Match attribute="value" or attribute='value' or attribute=value
    const attrRegex = /(\w+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
    let match;

    while ((match = attrRegex.exec(attrString)) !== null) {
      const [, name, doubleQuoted, singleQuoted, unquoted] = match;
      attributes[name.toLowerCase()] = doubleQuoted || singleQuoted || unquoted || '';
    }

    return attributes;
  };

  let elementIndex = 0;

  // Process each element type
  Object.entries(patterns).forEach(([tagName, regex]) => {
    let match;

    while ((match = regex.exec(html)) !== null) {
      let attributes: Record<string, string> = {};
      let content: string | undefined;

      if (tagName === 'script') {
        // Handle both self-closing and content script tags
        if (match[1]) {
          // Script with content: <script attrs>content</script>
          attributes = parseAttributes(match[1]);
          content = match[2] || undefined;
        } else if (match[3]) {
          // Self-closing script: <script attrs />
          attributes = parseAttributes(match[3]);
        }
      } else if (tagName === 'style' || tagName === 'title') {
        // Elements with content
        attributes = parseAttributes(match[1]);
        content = match[2] || undefined;
      } else {
        // Self-closing elements (link, meta)
        attributes = parseAttributes(match[1]);
      }

      const element: ParsedElement = {
        tagName,
        attributes,
        content,
        key: `head-element-ssr-${elementIndex}-${tagName}`,
      };

      elements.push(element);
      elementIndex++;
    }
  });

  return elements;
};

/**
 * Renders a parsed element as React component
 */
const renderElement = (element: ParsedElement) => {
  const { tagName, attributes, content, key } = element;

  switch (tagName) {
    case 'link':
      return React.createElement('link', { key, ...attributes });

    case 'meta':
      return React.createElement('meta', { key, ...attributes });

    case 'script':
      if (content) {
        return React.createElement('script', {
          key,
          ...attributes,
          dangerouslySetInnerHTML: { __html: content },
        });
      }
      return React.createElement('script', { key, ...attributes });

    case 'style':
      if (content) {
        return React.createElement('style', {
          key,
          ...attributes,
          dangerouslySetInnerHTML: { __html: content },
        });
      }
      return React.createElement('style', { key, ...attributes });

    case 'title':
      if (content) {
        return React.createElement('title', { key, ...attributes }, content);
      }
      return React.createElement('title', { key, ...attributes });

    default:
      return null;
  }
};

/**
 * Server-side safe component for rendering head elements from raw HTML
 * Works during SSR without requiring DOM APIs or useEffect
 *
 * Usage:
 * <Head>
 *   <HeadRawHtmlRenderer html={topPageData.headRawHtml} />
 * </Head>
 */
export const HeadRawHtmlRenderer: React.FC<HeadRawHtmlRendererProps> = ({ html }) => {
  if (!html || html.trim() === '') {
    return null;
  }

  try {
    const elements = parseHeadElementsSSR(html);

    if (elements.length === 0) {
      return null;
    }

    return <>{elements.map(renderElement)}</>;
  } catch (error) {
    console.error('HeadRawHtmlRenderer: Error parsing HTML:', error);
    return null;
  }
};
